import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { Select, Skeleton, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";

import { Chip, SectionCard, Term, fmtNum, useMasteryApi } from "./shared";

// The steward decision ledger — every audit entry across the queue, newest
// first. Per-item history still lives in the review modal; this is the
// portfolio-wide governance view.
//
// This is also the way back to a finished decision: a row links into the
// review queue with ?item=<id>, which opens that item's modal where Reopen
// reverses the decision. Gap items have no queue row, so they are not linked.

interface ActivityRow {
  id: number;
  item_id: string;
  entity_display_name: string;
  is_gap_item: boolean;
  action: string;
  action_value: string | null;
  notes: string;
  performed_at: string;
  performed_by: string | null;
  performed_by_name: string | null;
  writeback_status: string | null;
  writeback_target: string | null;
}

interface ActivityPayload {
  results: ActivityRow[];
  count: number;
  actions: string[];
  actors: string[];
}

const PAGE_SIZE = 50;

// resolved/accepted read as merges, rejections as declines, the rest neutral
const ACTION_COLOR: Record<string, string> = {
  accepted: "green",
  resolved: "green",
  not_a_match: "orange",
  rejected: "orange",
  dismissed: "default",
  deferred: "default",
  note_added: "blue",
};

const fmtWhen = (iso: string) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

export default function MasteryActivity() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [action, setAction] = useState<string>("");
  const [actor, setActor] = useState<string>("");

  const { data, isLoading } = useMasteryApi<ActivityPayload>(
    "mastery-activity/",
    {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      action: action || undefined,
      actor: actor || undefined,
    },
  );

  // Gap items are keyed GAP-<golden_id> and have no queue row to open.
  const openItem = useCallback(
    (row: ActivityRow) =>
      row.is_gap_item
        ? {}
        : {
            style: { cursor: "pointer" as const },
            onClick: () =>
              navigate(
                `/portfolio/data/mastery/queue?item=${encodeURIComponent(row.item_id)}`,
              ),
          },
    [navigate],
  );

  if (isLoading && !data) {
    return <Skeleton active paragraph={{ rows: 10 }} />;
  }

  const columns: ColumnsType<ActivityRow> = [
    {
      key: "performed_at",
      title: "When",
      dataIndex: "performed_at",
      width: 160,
      render: (v: string) => (
        <span className="whitespace-nowrap tabular-nums">{fmtWhen(v)}</span>
      ),
    },
    {
      key: "performed_by",
      title: "Steward",
      dataIndex: "performed_by_name",
      width: 150,
      render: (v: string | null, r) => v || r.performed_by || "—",
    },
    {
      key: "action",
      title: "Decision",
      dataIndex: "action",
      width: 140,
      render: (v: string) => (
        <Tag color={ACTION_COLOR[v] ?? "default"}>
          <Term term={v}>{v.replace(/_/g, " ")}</Term>
        </Tag>
      ),
    },
    {
      key: "entity",
      title: "Record",
      dataIndex: "entity_display_name",
      render: (v: string, r) => (
        <span>
          {v}
          {r.is_gap_item && <Chip>data gap</Chip>}
        </span>
      ),
    },
    {
      key: "notes",
      title: "Rationale",
      dataIndex: "notes",
      ellipsis: true,
      render: (v: string) => v || <span className="text-[#9aa9b0]">—</span>,
    },
  ];

  return (
    <SectionCard
      title="Decision log"
      meta="every steward decision across the queue, newest first"
      extra={
        <span className="flex items-center gap-2">
          <Select
            size="small"
            allowClear
            placeholder="Decision"
            value={action || undefined}
            onChange={(v) => {
              setAction(v ?? "");
              setPage(1);
            }}
            options={(data?.actions ?? []).map((a) => ({
              value: a,
              label: a.replace(/_/g, " "),
            }))}
            style={{ minWidth: 150 }}
          />
          <Select
            size="small"
            allowClear
            placeholder="Steward"
            value={actor || undefined}
            onChange={(v) => {
              setActor(v ?? "");
              setPage(1);
            }}
            options={(data?.actors ?? []).map((a) => ({ value: a, label: a }))}
            style={{ minWidth: 210 }}
          />
        </span>
      }
    >
      <Table<ActivityRow>
        size="small"
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={data?.results ?? []}
        onRow={openItem}
        scroll={{ y: 520 }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.count ?? 0,
          showSizeChanger: false,
          showTotal: (total) => `${fmtNum(total)} decisions`,
          onChange: setPage,
        }}
      />
    </SectionCard>
  );
}
